import { diffOps, type Op } from './wordDiff.ts';

/** One located error, as the wire carries it. */
export type Mark = {
  /** The stored `ErrorRecord`'s id — what `speak(errorRecordId:)` takes. */
  id: string;
  start: number;
  end: number;
  learnerForm: string;
  targetForm: string;
};

/** A run of the line, marked or not. */
export type Piece = { text: string; mark: Mark | null };

/**
 * Split a transcript line into marked and unmarked runs.
 *
 * The spans come from the server, which computed them against the transcript
 * it stored. This trusts the *line* over the span: anything out of range is
 * dropped rather than clamped, and the marked text is sliced from the line
 * rather than taken from the event, so a disagreement shows the learner what
 * they actually said.
 *
 * Overlaps drop rather than nest. Two marks over the same words cannot both be
 * rendered and a nested mark is not parseable by a reader; the debrief still
 * has both errors.
 */
/**
 * Words and the offsets they sit at, so a narrowed mark keeps real positions.
 */
function words(s: string): { text: string; at: number }[] {
  const out: { text: string; at: number }[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) out.push({ text: m[0], at: m.index });
  return out;
}

/**
 * Narrow a clause-sized mark to the words that actually changed.
 *
 * The extraction model returns whole clauses: "Yesterday I go to the park" →
 * "Yesterday I went to the park". Marking that verbatim underlines a sentence
 * and glosses it with another sentence, which teaches nothing and is not what
 * a recast is. A word-level LCS finds the runs that differ and marks those,
 * pairing each with the target words it lines up against.
 *
 * Falls back to the whole mark when the two share no words at all — a genuine
 * rewrite is one correction, and shattering it into per-word noise would be
 * worse than leaving it whole.
 */
function narrow(m: Mark): Mark[] {
  const a = words(m.learnerForm);
  const b = words(m.targetForm);
  if (a.length === 0 || b.length === 0) return [m];

  const n = a.length;
  const ops = diffOps(a, b, (x, y) => x.text.toLowerCase() === y.text.toLowerCase());
  // No word in common: a genuine rewrite is one correction, and shattering it
  // into per-word noise would be worse than leaving it whole.
  if (!ops.some((o) => o.kind === 'same')) return [m];

  // Each maximal run of non-`same` ops is one correction.
  const out: Mark[] = [];
  for (let p = 0; p < ops.length;) {
    if (ops[p].kind === 'same') {
      p++;
      continue;
    }
    const dels: number[] = [];
    const ins: number[] = [];
    let lastSameBefore = -1;
    for (let q = p - 1; q >= 0; q--) {
      if (ops[q].kind === 'del') {
        lastSameBefore = (ops[q] as { i: number }).i;
        break;
      }
      if (ops[q].kind === 'same') break;
    }
    while (p < ops.length && ops[p].kind !== 'same') {
      const op = ops[p];
      if (op.kind === 'del') dels.push(op.i);
      else if (op.kind === 'ins') ins.push(op.j);
      p++;
    }
    const targetForm = ins.map((x) => b[x].text).join(' ');
    if (dels.length > 0) {
      const first = a[dels[0]];
      const last = a[dels[dels.length - 1]];
      out.push({
        id: m.id,
        start: m.start + first.at,
        end: m.start + last.at + last.text.length,
        learnerForm: m.learnerForm.slice(first.at, last.at + last.text.length),
        // A pure deletion has nothing to show instead; the whole clause's
        // target is the only honest gloss left.
        targetForm: targetForm || m.targetForm,
      });
    } else if (ins.length > 0) {
      // An insertion has no learner word of its own — "I agree" → "I do
      // agree". Anchor it to the word before so the correction is still
      // reachable, or the first word when it lands at the start.
      const anchorIndex = lastSameBefore >= 0 ? lastSameBefore : findAnchor(ops, p);
      const w = a[Math.max(0, Math.min(anchorIndex, n - 1))];
      out.push({
        id: m.id,
        start: m.start + w.at,
        end: m.start + w.at + w.text.length,
        learnerForm: w.text,
        targetForm: `${w.text} ${targetForm}`.trim(),
      });
    }
  }
  return out.length > 0 ? out : [m];
}

/** The learner word a run of pure insertions should attach to. */
function findAnchor(ops: Op[], from: number): number {
  let seen = 0;
  for (let q = 0; q < from; q++) {
    if (ops[q].kind === 'same') seen++;
    else if (ops[q].kind === 'del') seen = (ops[q] as { i: number }).i + 1;
  }
  return Math.max(0, seen - 1);
}

/**
 * A short enough quote for an aria-label: the whole label is read as one
 * utterance, and a full turn of speech would make the trigger the longest
 * thing a screen reader meets in the transcript rather than the shortest.
 */
export function preview(text: string): string {
  return text.length > 60 ? `${text.slice(0, 60).trimEnd()}…` : text;
}

export function markUp(text: string, marks: Mark[]): Piece[] {
  const usable = marks
    .filter(
      (m) =>
        Number.isInteger(m.start) && Number.isInteger(m.end) && m.start >= 0 && m.end > m.start && m.end <= text.length,
    )
    .flatMap(narrow)
    .sort((a, b) => a.start - b.start);

  const pieces: Piece[] = [];
  let at = 0;
  for (const m of usable) {
    // An overlap with the previous mark: `at` has already passed its start.
    if (m.start < at) continue;
    if (m.start > at) pieces.push({ text: text.slice(at, m.start), mark: null });
    pieces.push({ text: text.slice(m.start, m.end), mark: m });
    at = m.end;
  }
  if (at < text.length) pieces.push({ text: text.slice(at), mark: null });
  // A line with no usable marks is still one piece, so the caller renders the
  // same shape either way.
  return pieces.length > 0 ? pieces : [{ text, mark: null }];
}

/**
 * Whether the tutor has been asked for words that have not started arriving.
 *
 * The opening turn made this visible. A greeting is produced the moment the
 * socket is accepted — before this screen has mounted, let alone subscribed —
 * and the event bus has no replay (`events.rs`), so the `StateChanged`
 * announcing it is usually broadcast to nobody. The screen would otherwise sit
 * on `idle`, under a button reading "Hold to talk", for a whole model call and
 * then start playing audio out of nowhere.
 *
 * `'thinking'` is the server's own word, lowercased where the event is handled;
 * pinning it here is the point of testing a one-line rule, because the two
 * spellings drifting apart is exactly the failure that shows up as a bubble
 * which never appears.
 *
 * The liveness of the session is the other half of the question, and it is not
 * implied by the turn state: a handshake refused at capacity answers `503` and
 * kills the socket before any state arrives at all, leaving a session that
 * opened on `thinking` — so the bubble would bounce above its own "Disconnected"
 * alert. A conversation ended before the greeting landed is the same lie under
 * a finished transcript. Nobody is coming in either case.
 */
export function awaitsFirstWord({
  state,
  reply,
  dead,
  ended,
}: {
  state: string;
  reply: string;
  dead: string | null;
  ended: boolean;
}): boolean {
  return state === 'thinking' && reply === '' && dead === null && !ended;
}

/**
 * Whether that wait is the models loading rather than the tutor thinking.
 *
 * The two are indistinguishable on screen and wildly different in length: a
 * warm chat call reaches its first token in about 1.7s, and a cold one — oMLX
 * pulling a 9B model off disk — was measured at 31s. Before the tutor spoke
 * first, a learner arriving mid-warm-up simply found the button slow to answer.
 * Now the greeting fires on its own the moment the socket opens, so they watch
 * a typing indicator for half a minute with nothing to distinguish it from a
 * tutor about to speak, or from a broken app.
 *
 * Composed from `awaitsFirstWord` rather than checked beside it: there is no
 * such thing as warming up while nothing is being waited for, and expressing
 * that as two independent booleans in the markup is how they drift apart.
 */
export function awaitsWarmUp(args: {
  state: string;
  reply: string;
  dead: string | null;
  ended: boolean;
  /** `Query.tutorReady`, read once when the screen mounted. */
  tutorReady: boolean;
}): boolean {
  return awaitsFirstWord(args) && !args.tutorReady;
}

/** The fields of a session event the preview reads. */
export type PartialEvent = { __typename: string; text?: string; state?: string };

/**
 * What the learner's live preview shows after `ev` (streaming-transcripts
 * PRD §5).
 *
 * The server sends the whole text so far, so a partial replaces rather than
 * appends, and an empty one is the server taking a preview away after a
 * cancel. Everything that ends an utterance without a cancel clears it here:
 * the committed line, an empty transcript, a failure. `IDLE` does not, because
 * a reply that ends while the learner talks over it publishes one.
 */
export function nextPartial(prev: string, ev: PartialEvent): string {
  switch (ev.__typename) {
    case 'PartialTranscript':
      return ev.text ?? '';
    case 'LearnerTurnCommitted':
    case 'SessionFailed':
      return '';
    case 'StateChanged':
      return ev.state === 'NEEDS_REPEAT' ? '' : prev;
    default:
      return prev;
  }
}
